import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import Button from "components/button";
import Input, { Textarea } from "components/input";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditFuelTypeForm from "./useEditFuelTypeForm";

export const editFuelTypePageRouteName = (id?: string) =>
  `/config/fuel-type/${id ?? ":id"}/edit`;

const EditFuelTypePage = () => {
  const { id } = useParams<{ id: string }>();
  const {
    handleSubmit,
    errors,
    register,
    control,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
  } = useEditFuelTypeForm(id);

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <Typography variant="bodyLarge">
              {t("label-fuel-type-name")}{" "}
              <span className="text-error-500">*</span>
            </Typography>
            <Input
              placeholder={t("label-fuel-type-name")}
              required
              {...register("fuelTypeName")}
              error={errors.fuelTypeName}
              data-testid="input-fuel-type-name-edit"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Typography variant="bodyLarge">
              {t("label-price-fuel")}{" "}
              <span className="text-error-500">*</span>
            </Typography>
            <Controller
              name="priceFuel"
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  enableCurrencyFormat
                  placeholder={t("label-price-fuel")}
                  required
                  {...field}
                  error={errors.priceFuel}
                  data-testid="input-price-fuel-edit"
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Typography variant="bodyLarge">
              {t("label-effective-date")}{" "}
              <span className="text-error-500">*</span>
            </Typography>
            <Input
              type="date"
              placeholder={t("label-effective-date")}
              required
              {...register("effectiveDate")}
              error={errors.effectiveDate}
              data-testid="datepicker-effective-date-edit"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Typography variant="bodyLarge">
              {t("label-description")}
            </Typography>
            <Textarea
              placeholder={t("label-description")}
              {...register("description")}
              error={errors.description}
              data-testid="textarea-fuel-description-edit"
            />
          </div>
        </form>
      </section>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={() => setShowCancelModal(true)}
          data-testid="btn-cancel-fuel-edit"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={handleSubmit(onSubmit)}
          data-testid="btn-save-fuel-edit"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-fuel-type-edit-modal"
      />

      {/* Discard Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-discard-title")}
        message={t("modal-discard-message")}
        confirmLabel={t("modal-confirm-discard")}
        cancelLabel={t("modal-confirm-stay")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="discard-fuel-type-edit-modal"
      />
    </div>
  );
};

export default EditFuelTypePage;
